import { mapGetters } from 'vuex';

import ScanProcess from '@/modules/scan-process';
import ApiActions from '@/modules/api-actions';
import i18n from '@/i18n';
import page from '@/mixins/page';
import measurements from '@/mixins/measurements';
import handleArmDetection from '@/mixins/arm-detection';
import reinitializeScan from '@/mixins/reinitialize-scan';
import { EVENTS } from '@/events/hardware';

const scanResults = {
  mixins: [page, handleArmDetection, measurements, reinitializeScan],
  async mounted() {
    this.stopListen();
    const { tid, scannerId } = ScanProcess;
    await Promise.all([this.getMeasurements(tid), ScanProcess.release()]);
    this.stopArmDetection(scannerId);
    this.$events.emit(EVENTS.START_BT_SCAN, true);
    setTimeout(() => {
      this.$events.emit(EVENTS.STOP_BT_SCAN);
    }, 300);
    if (!this.isHM) {
      this.autoplayMeasurements();
    }
    if (this.isContactless) {
      if (!this.isHM || this.measurementFailed) {
        await this.initScannerForArmDetection();
        this.showDoll = true;
        this.autoLeave(this.isHM ? 20 : 30);
      } else {
        this.autoLeave(5);
      }
    }
  },
  data: () => ({
    showDoll: false,
    activeTime: 500,
  }),
  methods: {
    async initScannerForArmDetection() {
      try {
        console.log('initializing scanner for arm detection');
        await this.reInitializeScan();
        this.startListen();
        this.startArmDetection();
      } catch (e) {
        console.log('failed to initScannerForArmDetection', e.message);
      }
    },
    autoLeave(sec = 30) {
      if (this.leaveTimer) {
        clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
      }
      this.leaveTimer = setTimeout(() => {
        this.leave();
      }, sec * 1000);
      this.$once('hook:beforeDestroy', () => {
        clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
      });
    },
    onMeasurementClick(index) {
      this.stopAutoplay();
      this.activeMeasurementIndex = index;
      this.setCurrentMeasurement(this.measurements[index]);
      this.autoLeave();
    },
    async getMeasurements(tid) {
      try {
        const data = await ScanProcess.waitMeasurement(tid);
        this.processMeasurements(data);
      } catch (e) {
        console.log(`[ERROR] ${e.message || 'scan failed for unknown reason'}`);
        this.measurementFailed = true;
      }
    },
    retry(count) {
      if (this.nothingYet) return;

      const retryCount = typeof count === 'number' ? count : 5;
      if (this.hasArmDetectionStarted && ScanProcess.isOngoing()) {
        if (this.isStamp) {
          this.$events.goto('StampScanBody');
        } else {
          this.$events.goto('ScanBody');
        }
        this.$events.stopLoading();
      } else if (retryCount > 0) {
        this.$events.startLoading();
        this.initScannerForArmDetection().then(() => {
          this.retry(retryCount - 1);
        });
      } else {
        this.$events.stopLoading();
        this.$events.alert(this.$t('welcome.unexpected_error'));
        this.leave();
      }
    },
    leave() {
      if (this.nothingYet) return;

      if (this.isContactless) {
        this.stopListen();
        if (ScanProcess.isOngoing()) {
          const { scannerId } = ScanProcess;
          ScanProcess.cancel().then(() => {
            this.stopArmDetection(scannerId);
            ApiActions.user.logout();
          });
        } else {
          ApiActions.user.logout();
        }
      }
      if (this.isStamp) {
        if (this.measurementFailed) {
          this.$events.goto('Welcome');
        } else {
          this.$events.goto('StampEnd');
        }
      } else if (ScanProcess.isMobile()) {
        this.$events.goto('HomePage');
      }
      else {
        this.$events.goto(this.isContactless ? 'Welcome' : 'HomePage');
      }
    },
  },
  computed: {
    ...mapGetters('mtmUser', ['isContactless']),
    ...mapGetters('user', ['objUrl']),
    panelHeight() {
      if (this.measurementFailed) return 'auto';

      if (this.isStamp) return 350;

      return '15%';
    },
    containerStyle() {
      const height =
        this.nothingYet || !this.measurementFailed ? `calc(100% - ${this.panelHeight})` : '100%';
      return {
        height,
      };
    },
    actions() {
      return [i18n.t('result.leave'), i18n.t('result.scan_again')];
    },
    nothingYet() {
      return !this.measurementFailed && !this.hasMeasurements;
    },
    bothReady() {
      return this.leftReady && this.rightReady;
    },
  },
  watch: {
    leftReady(nv) {
      if (nv) {
        if (!this.isStamp) return;

        if (this.measurementFailed) {
          this.leave();
        } else {
          this.retry();
        }
      }
    },
    rightReady(nv) {
      if (nv) {
        if (!this.isStamp) return;

        if (this.measurementFailed) {
          this.retry();
        } else {
          this.leave();
        }
      }
    },
    bothReady(nv) {
      if (nv && !this.isStamp) {
        this.retry();
      }
    },
  },
};

export default scanResults;
